import Vue from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';
import Home from '../views/Home.vue';
import SignUp from '../views/signUp/SignUpContainer.vue';
import ConfirmationCode from '../views/ConfirmationCode.vue';
import ChangePhone from '../views/ChangePhone.vue';
import CitySelection from '../views/CitySelection.vue';
import CourseSelection from '../views/CourseSelection.vue';
import CampusSelection from '../views/CampusSelection.vue';
import AdmissionTypeSelection from '../views/AdmissionTypeSelection.vue';
import EssayThemeSelection from '@/views/essay/EssayThemeSelection.vue';
import EssayThemeSupportText from '@/views/essay/EssayThemeSupportText.vue';
import EssaySend from '@/views/essay/EssaySend.vue';
import EnemYearSelection from '../views/enem/EnemYearSelection.vue';
import EnemSubscriptionNumber from '../views/enem/EnemSubscriptionNumber.vue';
import EnemGrades from '../views/enem/EnemGrades.vue';
import AdmissionResult from '../views/AdmissionResult.vue';
import TransferGraduationSendDocument from '../views/transferGraduation/TransferGraduationSendDocument.vue';
import SecondGraduationInstitutionInfo from '../views/secondGraduation/SecondGraduationInstitutionInfo.vue';
import TransferGraduationInstitutionInfo from '../views/transferGraduation/TransferGraduationInstitutionInfo.vue';
import SecondGraduationSendDocument from '../views/secondGraduation/SecondGraduationSendDocument.vue';
import pipeline from '@/pipeline';
import stepRouteMapping from '@/pipeline/stepRouteMapping';
import VIEWS_NAMES from './viewsNames';
import { AnalyticsActions } from '@/store/modules/analytics/actions';
import { AdmissionActions } from '@/store/modules/admission/actions';
import { continueMiddleware, hasCityInStoreMiddleware } from '../middleware';

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
  {
    path: '/',
    name: VIEWS_NAMES.HOME,
    component: Home,
  },
  {
    path: '/comecar',
    name: VIEWS_NAMES.SIGN_UP,
    component: SignUp,
  },
  {
    path: '/continuando',
    name: VIEWS_NAMES.SIGN_UP_OUT_OF_TEST,
    component: SignUp,
    meta: {
      middleware: continueMiddleware,
    },
  },
  {
    path: '/continuar',
    name: VIEWS_NAMES.SIGN_IN,
    redirect: { name: VIEWS_NAMES.SIGN_UP_OUT_OF_TEST },
  },
  {
    path: '/codigo-de-confirmacao',
    name: VIEWS_NAMES.CONFIRMATION_CODE,
    component: ConfirmationCode,
  },
  {
    path: '/troca-celular',
    name: VIEWS_NAMES.CHANGE_PHONE,
    component: ChangePhone,
  },
  { path: '/selecao-de-curso', name: VIEWS_NAMES.COURSE_SELECTION, component: CourseSelection },
  { path: '/selecao-de-cidade', name: VIEWS_NAMES.CITY_SELECTION, component: CitySelection },
  {
    path: '/selecao-de-polo',
    name: VIEWS_NAMES.CAMPUS_SELECTION,
    component: CampusSelection,
    meta: { middleware: hasCityInStoreMiddleware },
  },
  {
    path: '/forma-de-ingresso',
    name: VIEWS_NAMES.ADMISSION_TYPE_SELECTION,
    component: AdmissionTypeSelection,
  },
  {
    path: '/tema-de-redacao',
    name: VIEWS_NAMES.ESSAY_THEME_SELECTION,
    component: EssayThemeSelection,
  },
  {
    path: '/apoio-para-redacao',
    name: VIEWS_NAMES.ESSAY_THEME_SUPPORT_TEXT,
    component: EssayThemeSupportText,
  },
  { path: '/envio-de-redacao', name: VIEWS_NAMES.ESSAY_SEND, component: EssaySend },
  {
    path: '/selecao-de-ano-enem',
    name: VIEWS_NAMES.ENEM_YEAR_SELECTION,
    component: EnemYearSelection,
  },
  {
    path: '/inscricao-enem',
    name: VIEWS_NAMES.ENEM_SUBSCRIPTION_NUMBER,
    component: EnemSubscriptionNumber,
  },
  { path: '/notas-do-enem', name: VIEWS_NAMES.ENEM_GRADES, component: EnemGrades },
  {
    path: '/dados-da-primeira-graduacao',
    name: VIEWS_NAMES.SECOND_GRADUATION_INSTITUTION_INFO,
    component: SecondGraduationInstitutionInfo,
  },
  {
    path: '/dados-de-transferencia',
    name: VIEWS_NAMES.TRANSFER_GRADUATION_INSTITUTION_INFO,
    component: TransferGraduationInstitutionInfo,
  },
  {
    path: '/documentacao-da-primeira-graduacao',
    name: VIEWS_NAMES.SECOND_GRADUATION_SEND_DOCUMENT,
    component: SecondGraduationSendDocument,
  },
  {
    path: '/documentacao-da-transferencia',
    name: VIEWS_NAMES.TRANSFER_GRADUATION_SEND_DOCUMENT,
    component: TransferGraduationSendDocument,
  },
  { path: '/resultado', name: VIEWS_NAMES.ADMISSION_RESULT, component: AdmissionResult },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior: () => {
    return { x: 0, y: 0 };
  },
});
export interface Context {
  from: any;
  next: Function;
  router: VueRouter;
  to: any;
}

// Único lugar do guard onde o `next` do Vue Router é chamado, e todo caminho daqui o
// chama exatamente uma vez. Antes essa lógica vivia no corpo do `beforeEach` e resolvia
// a navegação ANTES de rodar a cadeia de middlewares, que então chamava `next` de novo.
export function resolveNavigation({ to, from, next }: Context, ...parameters: any[]) {
  // A cadeia de middlewares já decidiu a navegação (`next(false)`, `next('/rota')`):
  // repassa o que ela pediu e encerra, sem consultar o pipeline.
  if (parameters.length) return next(...parameters);

  const isRouteOnStateMachine = Object.values(stepRouteMapping).includes(to.path);

  if (pipeline.initialized) {
    const stepRoute = stepRouteMapping[pipeline.currentStep];

    if (stepRoute === to.path) return next();

    // Delegates back behavior to pipeline
    next(from.path);
    return pipeline.prev();
  }

  if (isRouteOnStateMachine) {
    // TODO: change to a fallback route
    return next('/');
  }

  return next();
}

// Fábrica recursiva do `next` entregue a cada middleware. Ela ENCADEIA: sem argumentos,
// segue para o middleware seguinte; com argumentos, encerra a cadeia repassando a decisão
// para o terminador (`resolveNavigation`). Middleware nenhum enxerga o `next` do router.
export function nextFactory(context: Context, middlewares: Function[], index: number): Function {
  return (...parameters: any[]) => {
    const subsequentMiddleware = middlewares[index];

    // Cadeia esgotada, ou middleware que já decidiu: quem chama `next` é o terminador.
    if (parameters.length || !subsequentMiddleware) {
      return resolveNavigation(context, ...parameters);
    }

    return subsequentMiddleware({
      ...context,
      next: nextFactory(context, middlewares, index + 1),
    });
  };
}

const hasQuery = (route: any) => {
  const queryValues = Object.values(route?.query || {});
  return !!queryValues.length;
};

const toMiddlewareList = (middleware: any): Function[] => {
  if (!middleware) return [];

  return Array.isArray(middleware) ? middleware : [middleware];
};

export function beforeEachGuard(to: any, from: any, next: Function) {
  const doAddQuery = !hasQuery(to) && hasQuery(from);
  if (doAddQuery) {
    // Early return preservado de propósito: trocar por `next({ ...to, query })` mudaria a
    // semântica de histórico num caminho que roda em TODA navegação com query string,
    // parâmetros de campanha incluídos. O efeito colateral conhecido é o
    // `Redirected when going from X to Y` no console do Vue Router 3 — ruído, não bug.
    return router.push({
      ...to,
      name: to?.name || '',
      query: { ...from.query, ...to.query },
    });
  }

  const context = { from, next, router, to };

  // A cadeia começa no índice 0 e é INVOCADA aqui: construir sem invocar deixava o
  // primeiro middleware fora da cadeia.
  return nextFactory(context, toMiddlewareList(to?.meta?.middleware), 0)();
}

router.beforeEach(beforeEachGuard);

// Rotas das etapas de admissão (redação, ENEM, segunda graduação, transferência):
// reverificar disponibilidade de curso/polo aqui reseta o campus e o histórico do
// pipeline (CHECK_ADMISSION_AVAILABILITIES), jogando o candidato de volta pra seleção
// de cidade no meio do preenchimento, sem aviso nenhum. A disponibilidade já foi
// checada ao entrar em admissionType.selection e volta a ser checada em /resultado.
const ADMISSION_TYPE_ROUTE_NAMES: string[] = [
  VIEWS_NAMES.ESSAY_THEME_SELECTION,
  VIEWS_NAMES.ESSAY_THEME_SUPPORT_TEXT,
  VIEWS_NAMES.ESSAY_SEND,
  VIEWS_NAMES.ENEM_YEAR_SELECTION,
  VIEWS_NAMES.ENEM_SUBSCRIPTION_NUMBER,
  VIEWS_NAMES.ENEM_GRADES,
  VIEWS_NAMES.SECOND_GRADUATION_INSTITUTION_INFO,
  VIEWS_NAMES.SECOND_GRADUATION_SEND_DOCUMENT,
  VIEWS_NAMES.TRANSFER_GRADUATION_INSTITUTION_INFO,
  VIEWS_NAMES.TRANSFER_GRADUATION_SEND_DOCUMENT,
];

router.afterEach((to, from) => {
  const { $store } = router.app;

  if (!$store) {
    return;
  }

  $store.dispatch(AnalyticsActions.ADMISSIONS_PAGE_VIEW);

  if (!ADMISSION_TYPE_ROUTE_NAMES.includes(to.name || '')) {
    $store.dispatch(AdmissionActions.CHECK_ADMISSION_AVAILABILITIES);
  }
});

export default router;
